import React, { useState, useEffect, useContext, ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link} from 'react-router-dom';
import { faArrowLeftLong, faCircleLeft, faCircleXmark, faDownload, faPaintBrush, faRobot, faRotateLeft, faUpload } from '@fortawesome/free-solid-svg-icons';
import { SVGGenerationArrowStaticBlue } from '../../components/svgs';
import { fileToBase64, urlToFile } from '../../utils/fileHandling';
import { useAiImageTypesGeneration } from '../../hooks/useAiImageTypesGeneration';
import { stateIconError, stateIconWaiting } from '../../types/ai/states';
import { downloadOriginal } from '../../utils/exportMedia';
import { AuthedImage } from '../../components/ui/AuthedImage';

export const ImageToImageInPaint = () => {
    const { t } = useTranslation('generation');
    const [imageInput,setImageInput] = useState<{file:any,src:string}|null>(null);
    const [imgMask,setImgMask] = useState<{file:any,src:string}|null>(null);
    const maskDisplayCanvasRef = useRef<HTMLCanvasElement>(null);
    const [maskHistory, setMaskHistory] = useState<string[]>([]);
    const [isDrawing, setIsDrawing] = useState(false);
    const [imageInpaintedStart, setImageInpaintedStart] = useState(false);
    const [maskVisible, setMaskVisible] = useState(true);
    const [editingMask, setEditingMask] = useState(true);

    const [eraserSize, setEraserSize] = useState(30);
    const [hoverPos, setHoverPos] = useState<{x:number, y:number} | null>(null);
    const imageCanvasRef = useRef<HTMLCanvasElement>(null);
    const maskCanvasRef = useRef<HTMLCanvasElement>(null);
    const [inputs,setInputs] = useState<{imgPromptPositive:string,imgPromptNegative:string}>({
        imgPromptPositive:'',
        imgPromptNegative: ''
    });
    const [imgInpainted, setImgInpainted] = useState<{file:any,src:string}|null>(null);
    const [generation,state,setState,getJobID,insufficientCreditsDialog] = useAiImageTypesGeneration();

    const handleGenerationResult = async() => {
        let img = generation[1];
        let imgFile = await urlToFile(img,img.replaceAll('\\','/').split('/').slice(-1)[0]);
        setImgInpainted({
            file:imgFile,
            src:img
        });
    };

    const handleImageInPaint = async() => {
        if(!imageInput || !imgMask) return;
        setState(null);
        const base64Image = await fileToBase64(imageInput.file);
        const base64Mask = await fileToBase64(imgMask.file);
        const body = { 
            'positive_prompt': inputs.imgPromptPositive, 
            // 'negative_prompt': inputs.imgPromptNegative,
            "image": base64Image,
            "mask": base64Mask
        };
        setState('send');
        let path = 'generate/image-to-image-inpaint'
        try {
            getJobID('image_gen',path,body);
        } catch(err){
            setState('error');
        };
    };

    const handleInputChange = (event:ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value;
        const name = event.target.name;
        setInputs(prev => ({...prev,[name]: value}));
    };
    const uploadFile = async(event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files ? event.target.files[0]  : null;
        if(file){
            const imgSrc = URL.createObjectURL(file);
            setImageInput({
                'file':file,
                'src':imgSrc
            });
        }
    };
    const setInitialMask = () => {
        //TODO: use full image size and adjust display image with mask and insure correct positions
        if(!imageInput) return;

        const img = new Image();
        img.src = imageInput.src;

        img.onload = () => {
            const imageCanvas = imageCanvasRef.current!;
            const maskCanvas = maskCanvasRef.current!;
            const displayCanvas = maskDisplayCanvasRef.current!;
            const ratio = img.width / img.height;
            const height = 520;
            const width = height * ratio;

            imageCanvas.width = width;
            imageCanvas.height = height;
            maskCanvas.width = width;
            maskCanvas.height = height;
            displayCanvas.width = width;
            displayCanvas.height = height;

            // draw image
            const ctxImg = imageCanvas.getContext("2d")!;
            ctxImg.drawImage(img, 0, 0, width,height);

            // create red mask
            const ctxMask = maskCanvas.getContext("2d")!;
            ctxMask.fillStyle = "rgba(255,0,0,1)";
            ctxMask.fillRect(0,0,width,height);

            //empty displaying mask
            const ctxDisplay = displayCanvas.getContext("2d")!;
            ctxDisplay.clearRect(0, 0, displayCanvas.width, displayCanvas.height);

            // Save mask to state
            updateMaskState();
        };
    };
    
    const download = () => {
        let resource = imgInpainted ? imgInpainted.src : '';
        if(resource === '') return;
        const fileName = resource.replaceAll('\\','/').split('/').slice(-1)[0];
        downloadOriginal(resource, fileName)
            .catch(err => console.error('Download fehlgeschlagen', err));
    };
    // Position Berechnung für Maus + Touch
    const getPos = (canvas: HTMLCanvasElement, evt: any) => {
        const rect = canvas.getBoundingClientRect();

        if (evt.touches) {
            // Touch
            return {
                x: evt.touches[0].clientX - rect.left,
                y: evt.touches[0].clientY - rect.top
            };
        } else {
            // Maus
            return {
                x: evt.clientX - rect.left,
                y: evt.clientY - rect.top
            };
        }
    };

    // Start Draw: Mouse
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!editingMask) return;

        // nur linke Taste
        if (e.buttons === 1) {
            setIsDrawing(true);
            const canvas = maskCanvasRef.current!;
            // eslint-disable-next-line no-restricted-syntax -- Undo-Verlauf der Masken-Zeichnung (kein Download), Art. 50 KI-VO nicht einschlaegig
            const snapshot = canvas.toDataURL("image/png");
            setMaskHistory(prev => [...prev, snapshot]);
            handleDraw(e); // ersten Punkt sofort radieren
        }
    };

    const handleMouseUp = () => {
        setIsDrawing(false);
        renderMaskDisplay();
    };

    const handleMouseLeave = () => {
        setIsDrawing(false);
        setHoverPos(null); // Kreis ausblenden
    };

    // Zeichnen über Maus
    const handleMouseMove = (e: React.MouseEvent) => {
        const canvas = maskCanvasRef.current!;
        const rect = canvas.getBoundingClientRect();

        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Hover-Position updaten
        setHoverPos({ x, y });

        // nur radieren wenn Maustaste gedrückt ist
        if (isDrawing && editingMask) {
            handleDraw(e);
        }
    };

    // ----------------------------------------------
    // Touch Events
    // ----------------------------------------------
    const handleTouchStart = (e: React.TouchEvent) => {
        if (!editingMask) return;
        setIsDrawing(true);
        const canvas = maskCanvasRef.current!;
        // eslint-disable-next-line no-restricted-syntax -- Undo-Verlauf der Masken-Zeichnung (kein Download), Art. 50 KI-VO nicht einschlaegig
        const snapshot = canvas.toDataURL("image/png");
        setMaskHistory(prev => [...prev, snapshot]);
        handleDraw(e);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (!editingMask || !isDrawing) return;
        e.preventDefault(); // verhindert Scrollen
        handleDraw(e);
    };

    const handleTouchEnd = () => {
        setIsDrawing(false);
        renderMaskDisplay();
    };

    const handleTouchCancel = () => {
        setIsDrawing(false);
    };

    // ----------------------------------------------
    // Zeichenfunktion (Radierer)
    // ----------------------------------------------
    const handleDraw = (evt: any) => {
        const maskCanvas = maskCanvasRef.current!;
        const displayCanvas = maskDisplayCanvasRef.current!;
        const pos = getPos(maskCanvas, evt);

        // Radieren auf Masken-Canvas
        const ctxMask = maskCanvas.getContext("2d")!;
        ctxMask.globalCompositeOperation = "destination-out";
        ctxMask.beginPath();
        ctxMask.arc(pos.x, pos.y, eraserSize, 0, Math.PI * 2);
        ctxMask.fill();

        // Radierte Fläche auf Anzeige-Canvas sichtbar machen
        const ctxDisplay = displayCanvas.getContext("2d")!;
        ctxDisplay.globalCompositeOperation = "source-over";
        ctxDisplay.fillStyle = "rgba(255,140,0,1)";
        ctxDisplay.beginPath();
        ctxDisplay.arc(pos.x, pos.y, eraserSize, 0, Math.PI * 2);
        ctxDisplay.fill();

        // State aktualisieren
        updateMaskState();
    };
    const updateMaskState = () => {
        const maskCanvas = maskCanvasRef.current!;
        // eslint-disable-next-line no-restricted-syntax -- internes Masken-State-Update (kein Download), Art. 50 KI-VO nicht einschlaegig
        const dataUrl = maskCanvas.toDataURL("image/png");
        fetch(dataUrl)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], "mask.png", { type: "image/png" });
                setImgMask({ file, src: dataUrl });
            });
    };
    const undoMask = () => {
        const canvas = maskCanvasRef.current!;
        const ctx = canvas.getContext("2d")!;

        setMaskHistory(prev => {
            if (prev.length === 0) return prev; // nichts zum Undo

            const previous = prev[prev.length - 1]; // letztes Snapshot

            // Bild wiederherstellen
            const img = new Image();
            img.src = previous;
            img.onload = () => {
                ctx.globalCompositeOperation = "source-over";
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0);
                updateMaskState(); // maskState aktualisieren
                renderMaskDisplay();
            };

            // letzten Eintrag aus dem Stack entfernen
            return prev.slice(0, prev.length - 1);
        });
    };
    const resetMask = () => {
        //reset history and set initial mask 
        setMaskHistory([]); 
        setInitialMask();
    };
    const renderMaskDisplay = () => {
        if (!maskCanvasRef.current || !maskDisplayCanvasRef.current) return;

        const maskCanvas = maskCanvasRef.current;
        const displayCanvas = maskDisplayCanvasRef.current;

        displayCanvas.width = maskCanvas.width;
        displayCanvas.height = maskCanvas.height;

        const ctxMask = maskCanvas.getContext("2d")!;
        const ctxDisplay = displayCanvas.getContext("2d")!;

        const maskData = ctxMask.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
        const data = maskData.data;

        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            if (r === 255) {
                // Maskenbereich = original (nicht radiert) → unsichtbar
                data[i + 3] = 0;
            } else {
                // Radierte Bereiche sichtbar als Grau
                data[i] = 255; //R
                data[i + 1] = 140; //G
                data[i + 2] = 0; //B
                data[i + 3] = 255; //Opaque
            }
        }

        ctxDisplay.putImageData(maskData, 0, 0);
    };
    

    useEffect(()=>{
        if(generation.length > 0){
            handleGenerationResult();
        }
    },[generation]);
    useEffect(() => {
        setImageInpaintedStart(false);
        //create mask
        setInitialMask();
    }, [imageInput]);

  return (
    <>
        {insufficientCreditsDialog}
        <div className="container-fluid">
            <div className='d-flex flex-column h-100'>
                <div className='row mx-0'>
                    <div className='col-auto p-2 pe-0'>
                        <Link 
                            to="/executions" 
                            className="btn btn-outline-secondary btn-sm ms-3"
                        >
                        <FontAwesomeIcon
                                icon={faArrowLeftLong}
                                size="1x"
                                title={t('back')}
                            />
                        </Link>
                    </div>
                </div>
                <div className='flex-grow-1 p-3 overflow-auto'>
                    <div className='row'>
                        <div className='col-6'></div>
                        <div className='col-5'>
                            {state
                                ? state === 'send'
                                    ? <p className='h4'>{t('state.sending')}</p>
                                    : state === 'waiting'
                                        ? <p className='h4'><span className="spin">{stateIconWaiting}</span> {t('state.waiting')}</p>
                                        : state === 'error'
                                            ? <p className='h4'>{stateIconError} {t('state.error')}</p>
                                                : <></>
                                : <></>
                            }
                        </div>
                    </div>
                    
                    <div className='row py-2 text-center align-items-center'>
                        <div className='col-5 d-flex flex-wrap p-auto  justify-content-center'>
                            { imageInput  &&
                                <>
                                    <div className='row w-100'>
                                        <div className='col-auto'>
                                            <p className='h4'>{t('inpaint.imageToInpaint')}</p>
                                        </div>    
                                        <div className='col-auto'>
                                            <button 
                                                    className="btn border-0 bg-transparent"
                                                    onClick={() => {
                                                        if(maskVisible === false){
                                                            setMaskVisible(true);
                                                            setEditingMask(true);
                                                        } else {
                                                            setEditingMask(false);
                                                            setMaskVisible(false);
                                                        }
                                                    }}
                                                    title={maskVisible ? t('inpaint.showMask') : t('inpaint.hideMask')}
                                                >
                                                    <FontAwesomeIcon 
                                                        icon={faPaintBrush} 
                                                        size='xl'
                                                    />
                                                    
                                                </button>
                                        </div>
                                        
                                    </div>
                                    {maskVisible && editingMask && (
                                        <div className='row mt-2'>
                                            <div className='col-auto'>
                                                <label className='me-2 text-secondary'>{t('inpaint.brushSize')}</label>
                                                <input 
                                                    type="number" 
                                                    min="5" 
                                                    max="100" 
                                                    value={eraserSize}
                                                    className='border-0 bg-transparent text-secondary'
                                                    onChange={e => setEraserSize(parseInt(e.target.value))}
                                                />
                                            </div>
                                            <div className='col-auto me-3'>
                                                <input 
                                                type="range" 
                                                min="5" 
                                                max="100" 
                                                value={eraserSize}
                                                onChange={e => setEraserSize(parseInt(e.target.value))}
                                            />
                                            </div>
                                            <div className='col-auto me-3'>
                                                <button 
                                                    className="btn border-0 bg-transparent text-secondary"
                                                    onClick={undoMask}
                                                    disabled={maskHistory.length === 0}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faRotateLeft}
                                                        className='me-2'
                                                        size='xl'
                                                    />
                                                    {t('inpaint.undo')}
                                                </button>
                                            </div>
                                            <div className='col-auto'>
                                                <button 
                                                    className="btn border-0 bg-transparent text-secondary"
                                                    onClick={resetMask}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCircleXmark}
                                                        className='me-2'
                                                        size='xl'
                                                    />
                                                    {t('inpaint.reset')}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <div className='row'>
                                        <div className='p-0' style={{ position:"relative", display:"inline-block" }}>
                                            {maskVisible && editingMask && hoverPos  && !isDrawing &&  (
                                                <div
                                                    style={{
                                                        position: "absolute",
                                                        top: hoverPos.y - eraserSize,
                                                        left: hoverPos.x - eraserSize,
                                                        width: eraserSize * 2,
                                                        height: eraserSize * 2,
                                                        borderRadius: "50%",
                                                        border: "2px solid #ffffffaa",  // halbtransparent weiß
                                                        pointerEvents: "none",          // Maus klickt durch
                                                        zIndex: 20
                                                    }}
                                                ></div>
                                            )}
                                            {/* Base Image */}
                                            <canvas 
                                                ref={imageCanvasRef} 
                                                style={{ display: imageInput ? "block" : "none"}} 
                                            />

                                             <canvas
                                                ref={maskDisplayCanvasRef}
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: 0,
                                                    pointerEvents: "none",
                                                    zIndex: 10,
                                                    opacity: maskVisible ? 0.4 : 0,
                                                }}
                                            />
                                            <canvas
                                                ref={maskCanvasRef}
                                                style={{
                                                    position:"absolute",
                                                    top:0,
                                                    left:0,
                                                    opacity: 0,
                                                    pointerEvents: editingMask ? "auto" : "none",
                                                    cursor: editingMask ? "crosshair" : "default"
                                                }}
                                                onMouseDown={handleMouseDown}
                                                onMouseMove={handleMouseMove}
                                                onMouseUp={handleMouseUp}
                                                onMouseLeave={handleMouseLeave}

                                                onTouchStart={handleTouchStart}
                                                onTouchMove={handleTouchMove}
                                                onTouchEnd={handleTouchEnd}
                                                onTouchCancel={handleTouchCancel}
                                            />

                                        </div>
                                    </div>
                                </>
                            }
                        </div>
                        <div className='col-1'> 
                            {
                                imageInpaintedStart
                                ? <SVGGenerationArrowStaticBlue/>
                                : <></>
                            }

                        </div>
                        <div className='col-5 d-flex flex-wrap p-auto justify-content-center'>
                            { imgInpainted &&
                                <>
                                    <div className='row w-100'>
                                        <div className='col-auto'>
                                            <p className='h4 me-2'>{t('generatedImage')} </p>
                                        </div>
                                        <div className='col-auto'>
                                            <button
                                                type="button" 
                                                className="btn btn-chat border-0 bg-transparent text-secondary p-0 m-0"
                                                data-bs-toggle="tooltip" 
                                                data-bs-placement="top" 
                                                title={t('download.image')}
                                                >
                                                <FontAwesomeIcon
                                                    icon={faDownload}
                                                    onClick={e =>download()}
                                                    size="xl"
                                                    className="pe-1"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    <div className='row mt-2'>
                                        <AuthedImage
                                            src={imgInpainted.src}
                                            alt={`Inpainted image`}
                                            className={'rounded d-block'}
                                            style={{height:'520px', width: 'auto', maxWidth:'100%'}}
                                        />
                                    </div>
                                </>
                            }
                        </div>
                    </div>
                </div>
                    
                <div className='row p-3'>
                    <div className='col-6'>
                        <div className='row mt-2'>
                            <div className='col-auto'>
                                <label htmlFor="fileuploadImage" className=' fw-bold me-2'>
                                    <FontAwesomeIcon icon={faUpload} className='me-2' />
                                    {t('uploadImage')}
                                </label>
                                <input
                                id = 'fileuploadImage'
                                type= 'file'
                                accept='image/png,image/jpg,image/jpeg'
                                onChange={event => uploadFile(event)}
                                hidden
                                />
                                {
                                    imageInput
                                    ? imageInput.file.name
                                    : <></>
                                }
                            </div>
                    
                        </div>
                        <div className='row mt-2'>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="imgPromptPositive"
                                name='imgPromptPositive'
                                rows={5}
                                value={inputs.imgPromptPositive}
                                onChange={(e) => handleInputChange(e)}
                                placeholder={t('inpaint.placeholderChange')}
                            />
                            </div>
                            <div className='col-5'>
                                <textarea
                                className="form-control"
                                id="imgPromptNegative"
                                name='imgPromptNegative'
                                rows={5}
                                value={inputs.imgPromptNegative}
                                onChange={(e) => handleInputChange(e)}
                                placeholder={t('inpaint.placeholderKeep')}
                            />
                            </div>
                            <div className='col-auto  d-flex flex-column justify-content-center text-start'>
                                <button
                                    className="btn btn-primary"
                                    title={t('inpaint.inpaintImage')}
                                    onClick={() => handleImageInPaint()}
                                >
                                    <FontAwesomeIcon
                                        icon={faRobot}
                                        size='xl'
                                        className='bg-transparent me-2'
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </>
  );
};

export default ImageToImageInPaint;